import Button from 'ext-ui/components/Button/Button'
import Modal from 'ext-ui/components/Modal/Modal'
import React from 'react'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  formatStatementAmount,
  formatStatementDate,
  formatStatementDateShort,
  formatStatementPeriod,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.format'
import {
  isStatementPaid,
  type StatementSummary,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import * as style from 'src/page-components/Accounts/AccountStatementsTab/components/StatementDetailModal/StatementDetailModal.style'
import { useStatementStatusLabel } from 'src/page-components/Accounts/AccountStatementsTab/useStatementLabels'
import { useStatementTransactions } from 'src/page-components/Accounts/AccountStatementsTab/useStatementTransactions'

interface StatementDetailModalProps {
  open: boolean
  onClose: () => void
  statement: StatementSummary
  /** Card label for the modal description, e.g. "T&E Card •• 5678". */
  cardLabel: string
  /** The card whose line items to read. Unused for a demo statement. */
  creditCardId: string | undefined
  /** True when `statement` is the fabricated fallback rather than a real read. */
  isDemoStatement: boolean
}

/**
 * Statement detail for one statement period (EX-40600).
 *
 * Reads like a downloaded statement rather than a transaction browser: the body
 * is text-only — figures and a date/description/amount ledger, with no merchant
 * avatars, category chips, or per-transaction actions. That is deliberate; this
 * is not an entry point into transaction detail.
 *
 * `Modal` supplies the focus trap, Escape close, body-scroll lock, and focus
 * restoration, so the row's View control gets focus back on close.
 */
const StatementDetailModal: React.FC<StatementDetailModalProps> = ({
  open,
  onClose,
  statement,
  cardLabel,
  creditCardId,
  isDemoStatement,
}) => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()

  // Fetched here rather than with the statement list: line items are their own
  // paginated route, and this modal is mounted only while open. `total` comes
  // from the seam rather than being summed over the rows below — for a real
  // statement the authoritative figure is the server's own `statementAmount`.
  const { transactions, isLoading, hasError, isTruncated, total } =
    useStatementTransactions(creditCardId, statement, isDemoStatement)

  const period = formatStatementPeriod(statement.period)
  const isPaid = isStatementPaid(statement.status)
  const statusLabel = useStatementStatusLabel(statement.status)

  return (
    <>
      {/*
        Safe to mount unconditionally: the row only renders this component while
        its statement is open, so at most one instance exists at a time —
        styled-components warns when the same createGlobalStyle mounts twice.
      */}
      <style.StatementDetailModalGlobalStyle />
      <Modal
        className={style.STATEMENT_DETAIL_MODAL_CLASS}
        open={open}
        onClose={onClose}
        title={translate('${period} statement', { period })}
        description={translate('${cardLabel} · Due ${dueOn} · ${status}', {
          cardLabel,
          dueOn: formatStatementDate(statement.dueOn),
          status: statusLabel,
        })}
        footer={
          <div css={style.footer}>
            <Button onClick={onClose}>{translate('Close')}</Button>
          </div>
        }
      >
        <div css={style.summary} data-testid="statementDetailSummary">
          <div css={style.summaryRow}>
            <span css={style.summaryLabel}>
              {translate('Statement amount')}
            </span>
            <span css={style.summaryValue}>
              {formatStatementAmount(formatCurrency, statement.statementAmount)}
            </span>
          </div>
          <div css={style.summaryRow}>
            <span css={style.summaryLabel}>{translate('Amount owed')}</span>
            <span css={style.summaryValue}>
              {formatStatementAmount(formatCurrency, statement.amountOwed)}
            </span>
          </div>
          {/*
        Same row anatomy either way; only the label and figure change. A paid
        statement reports what was paid, an open one restates what is due and
        when.
      */}
          <div css={style.summaryRow}>
            <span css={style.summaryLabel}>
              {isPaid
                ? translate('Amount paid')
                : translate('Payment due ${dueOn}', {
                    dueOn: formatStatementDateShort(statement.dueOn),
                  })}
            </span>
            <span css={style.summaryValue}>
              {formatStatementAmount(
                formatCurrency,
                isPaid
                  ? (statement.amountPaid ?? statement.amountOwed)
                  : statement.amountOwed,
              )}
            </span>
          </div>
        </div>

        {/*
          Tabular data, so it carries table semantics. It is not a real
          <table> because only the transaction rows may scroll (a design
          guardrail) and a scrollable <tbody> needs `display: block`, which
          breaks column alignment with the header and total. ARIA roles over
          the grid give the same structure without that fight — rows and
          rowgroups are both valid children of role="table".
        */}
        <div
          role="table"
          aria-label={translate('${period} statement transactions', {
            period,
          })}
        >
          <div css={style.ledgerHeader} role="row">
            <span css={style.ledgerHeaderCell} role="columnheader">
              {translate('Date')}
            </span>
            <span css={style.ledgerHeaderCell} role="columnheader">
              {translate('Description')}
            </span>
            <span css={style.ledgerHeaderAmountCell} role="columnheader">
              {translate('Amount')}
            </span>
          </div>
          {/*
            Only this group scrolls — see the note on `ledgerScroll`. It is
            focusable because a scrollable region must be reachable by
            keyboard (WCAG 2.1.1), and named so it is not an unlabelled tab
            stop.
          */}
          <div
            css={style.ledgerScroll}
            role="rowgroup"
            // A scrollable region must be keyboard-reachable (WCAG 2.1.1).
            // The rule does not model that exception, and the alternative is
            // a ledger only a mouse can scroll.
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            aria-label={translate('Transactions')}
          >
            {/*
              One row carries every non-ready state, so the ledger's table
              semantics stay intact — a bare <p> here would be an invalid child
              of role="rowgroup" and would break the row count for a screen
              reader. `aria-live` announces the transition for anyone who
              opened the modal before the fetch resolved.
            */}
            {(isLoading || hasError || transactions.length === 0) && (
              <div css={style.ledgerRow} role="row">
                {/*
                  `aria-colspan` because the ledger's other rows carry three
                  cells — a lone cell in a three-column table desyncs the cell
                  count for a screen reader, the same hazard the total row's
                  empty cell below exists to avoid.
                */}
                <span role="cell" aria-colspan={3} aria-live="polite">
                  {isLoading && translate('Loading transactions…')}
                  {!isLoading &&
                    hasError &&
                    translate('Transactions could not be loaded.')}
                  {!isLoading &&
                    !hasError &&
                    translate('No transactions on this statement.')}
                </span>
              </div>
            )}
            {/*
              A capped ledger renders the statement's real total above an
              incomplete row list, so the gap is explained rather than left
              looking like arithmetic that does not add up.
            */}
            {isTruncated && (
              <div css={style.ledgerRow} role="row">
                <span role="cell" aria-colspan={3}>
                  {translate(
                    'Showing the first ${count} charges on this statement.',
                    { count: String(transactions.length) },
                  )}
                </span>
              </div>
            )}
            {transactions.map((transaction) => (
              <div key={transaction.id} css={style.ledgerRow} role="row">
                <span css={style.ledgerDate} role="cell">
                  {formatStatementDateShort(transaction.postedOn)}
                </span>
                <span css={style.ledgerDescription} role="cell">
                  {transaction.description}
                </span>
                <span css={style.ledgerAmount} role="cell">
                  {formatStatementAmount(formatCurrency, transaction.amount)}
                </span>
              </div>
            ))}
          </div>
          <div css={style.ledgerTotal} role="row">
            {/* Empty cell, not aria-hidden — a hidden cell desyncs the count. */}
            <span role="cell" />
            <span role="cell">{translate('Total charges')}</span>
            <span css={style.ledgerAmount} role="cell">
              {formatStatementAmount(formatCurrency, total)}
            </span>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default StatementDetailModal
